// Wire protocol + role model shared between server and client. Built up
// incrementally stage by stage (lobby first, then dungeon/hero/minion state,
// then leveling and the debug god-mode toggle) rather than speccing the
// full protocol up front.

import type { PieceType } from "./pieces";

export type Role = "architect" | "hero1" | "hero2" | "hero3";

export const ROLES: readonly Role[] = ["architect", "hero1", "hero2", "hero3"];

export function isHeroRole(role: Role): boolean {
  return role !== "architect";
}

export type Phase = "lobby" | "playing" | "gameover";

export type Winner = "architect" | "heroes";

export type ArchitectDirection = "up" | "down" | "left" | "right";

/** A raw (not-necessarily-normalized) movement direction; the server normalizes it. */
export interface MoveVector {
  x: number;
  y: number;
}

/** The stat a hero's owning player picks on level-up — no spells yet, so this is the whole choice (see design doc's plan notes). */
export type LevelUpStat = "moveSpeed" | "maxHp" | "attackDamage";

/** The three hero classes from the design doc. Chosen per-hero in the lobby before a match can start. */
export type HeroClass = "warrior" | "hunter" | "mage";

export const HERO_CLASSES: readonly HeroClass[] = ["warrior", "hunter", "mage"];

/** A hero has 3 spell slots (design doc). Bound to Q/E/R on the client. */
export type SpellSlot = 1 | 2 | 3;

/** A world-space point in tile units — same coordinate space as a hero's x/y (see server/heroes.ts). */
export interface WorldPoint {
  x: number;
  y: number;
}

/** Gold-spend abilities available to the architect (design doc's "Target shapes" section). */
export type ArchitectAbility = "meteor" | "spawnMinions" | "spawnSuperMinion";

/** Permanent, stacking gold-spend upgrades to all future minions (design doc's "Target shapes" section). `minionCount` bumps how many minions spawn per group — both from a map minion marker and from the "spawnMinions" ability (not the super-minion spawn, which is always exactly one). */
export type ArchitectUpgrade = "minionSpeed" | "minionHealth" | "minionDamage" | "minionCount";

/** Special cells a piece may carry (design doc extension — doors/keys/chests). At most one per piece. */
export type SpecialCellKind = "door" | "key" | "chest";

/** Messages sent from a client to the server. */
export type ClientMessage =
  | { type: "claimRole"; role: Role }
  /** Hero-only, lobby-only: picks (or changes) this hero's class. A match can't start until every claimed hero has one. */
  | { type: "selectClass"; class: HeroClass }
  /**
   * Lobby-only: any connected player requests the match start. A no-op
   * server-side unless the roster is already valid (architect + at least
   * one classed hero) — the room no longer auto-starts the instant that
   * becomes true, players press this explicitly.
   */
  | { type: "startGame" }
  | { type: "architectMove"; dir: ArchitectDirection }
  | { type: "architectRotate" }
  | { type: "architectPlace" }
  /**
   * Arms/uses a gold-spend ability at a world point (client arms via a
   * hotkey, then confirms with a click — see src/input.ts). The server is
   * the sole judge of affordability; an unaffordable or malformed request is
   * just ignored, same as any other invalid input in this protocol.
   */
  | { type: "architectUseAbility"; ability: ArchitectAbility; point: WorldPoint }
  | { type: "architectBuyUpgrade"; upgrade: ArchitectUpgrade }
  | { type: "heroInput"; move: MoveVector }
  /** Right-click: walk straight toward this point each tick until arrival, a WASD key is pressed, or a new heroMoveTo/heroInput overrides it (see server/heroes.ts's moveTarget). */
  | { type: "heroMoveTo"; point: WorldPoint }
  /** Continuous world-space cursor position, throttled client-side (see src/main.ts) — used to aim skill-shots/glyphs/leaps. */
  | { type: "heroAim"; point: WorldPoint }
  | { type: "heroCastSpell"; slot: SpellSlot }
  | { type: "levelUpChoice"; stat: LevelUpStat }
  /**
   * Debug/testing toggle available to either role — a hero becomes immune
   * to all damage (flame barrier + minions); the architect freezes the
   * checkpoint timer. Each protects that role from its own loss condition,
   * making it much easier to poke at the rest of the game without losing
   * mid-test. Not part of the design doc — added for playtesting.
   */
  | { type: "toggleGodMode" }
  /**
   * Debug/testing only, and only take effect while the sender's own God
   * Mode is on (checked server-side, same spirit as toggleGodMode being a
   * pure playtesting aid — see CLAUDE.md's "Debug tools"). Architect-only.
   */
  | { type: "debugGrantGold" }
  | { type: "debugSetNextPiece"; pieceType: PieceType }
  /** Debug/testing only, God-Mode-gated, hero-only — see debugGrantGold. */
  | { type: "debugRefreshCooldowns" }
  | { type: "debugGrantLevelXp" }
  /**
   * Forces the room back to a fresh lobby (all roles released) from any
   * state — a manual escape hatch alongside the automatic one (see
   * server/room.ts's Room.close handler): if everyone who held a role
   * disconnects mid-match, new joiners would otherwise be stuck as
   * permanent spectators with no way to claim a role or restart, since
   * roles only lock/unlock via the normal lobby flow.
   */
  | { type: "returnToLobby" };

/** [col, row] tile coordinates — arrays over objects to keep snapshots small on the wire. */
export type TileCoord = [number, number];

/** A faded 4-tile target shape (design doc's "Target shapes"): covering every cell with floor earns the architect gold. Removed from the snapshot once completed. */
export interface TargetShapeSnapshot {
  id: number;
  cells: TileCoord[];
}

/** A hazard glyph baked into a placed floor tile — "burst" is a one-time trap (consumed on trigger), "dot" is a permanent damage zone. Always visible. */
export interface HazardGlyphSnapshot {
  cell: TileCoord;
  kind: "burst" | "dot";
}

export interface DungeonSnapshot {
  width: number;
  floorTiles: TileCoord[];
  foundations: TileCoord[];
  minionMarkers: TileCoord[];
  targetShapes: TargetShapeSnapshot[];
  /** Rows that have been fully built across (a tetris-style "line") and already earned their gold — rendered with a distinct tint. */
  goldLineRows: number[];
  /** Door tiles — block heroes only (not minions); removed once a key-holding hero gets close. */
  doors: TileCoord[];
  /** Unclaimed key pickups sitting on the floor. */
  keys: TileCoord[];
  /** Placed, uncollected chests, with the gold amount the architect will lose if a hero reaches one. */
  chests: { cell: TileCoord; goldAmount: number }[];
  hazardGlyphs: HazardGlyphSnapshot[];
  frontierRow: number;
  activePiece: {
    type: PieceType;
    cells: TileCoord[];
    isExplosive: boolean;
    /** Absolute position of the piece's door/key/chest cell, if it has one — see shared/types.ts's SpecialCellKind. */
    specialCell: { cell: TileCoord; kind: SpecialCellKind } | null;
  };
  nextPieceType: PieceType;
  nextPieceIsExplosive: boolean;
  checkpoint: TileCoord;
  checkpointTimerRemaining: number;
  /** Rows past whichever living hero is furthest along that a piece isn't allowed to be *placed* beyond — see CONFIG.architectAheadOfHeroesRows. Rendered as a dashed line on the architect's own screen; a piece can still be freely moved/rotated past it, placement just fails there. */
  virtualFrontierRow: number;
}

/** Status-effect kinds applied by spells (design doc's per-effect "duration"/"data"). Magnitude's meaning depends on kind — see server/statuses.ts. */
export type StatusKind = "dot" | "hot" | "slowed" | "damageReduction" | "attackRateBuff" | "moveSpeedBuff" | "rangeBuff";

export interface StatusSnapshot {
  kind: StatusKind;
  remaining: number;
}

/** A hero's position in continuous world coordinates (tile units — see server/heroes.ts). */
export interface HeroSnapshot {
  role: Role;
  class: HeroClass;
  x: number;
  y: number;
  hp: number;
  maxHp: number;
  level: number;
  xp: number;
  xpToNextLevel: number;
  /** How many level-up stat choices this hero has queued up but not yet made. */
  pendingLevelUps: number;
  godMode: boolean;
  /** Seconds remaining before each spell slot (1-indexed via SpellSlot, 0-indexed here) can be cast again. */
  spellCooldowns: [number, number, number];
  statuses: StatusSnapshot[];
  /** Holding a key (design doc extension) — at most one at a time, consumed opening a nearby door. */
  hasKey: boolean;
  /** Effective auto-attack range in tiles (base class range + any rangeBuff status) — shown as a circle for the first few seconds of a match (see CONFIG.heroAttackRangeShowSeconds). */
  attackRange: number;
  /** 0 = just attacked (on full cooldown), 1 = ready to attack again — drives the small cooldown bar shown above every unit (see src/render.ts). */
  attackCooldownPct: number;
  /** Seconds since this hero died; 0 while alive. Drives the auto-respawn countdown and the corpse-revive deadline (see CONFIG.heroAutoRespawnSeconds / heroCorpseReviveDeadlineSeconds). */
  timeSinceDeath: number;
  /** Seconds a living hero has been continuously standing next to this corpse, toward CONFIG.heroCorpseReviveSeconds; 0 while alive or when nobody is currently reviving it. */
  reviveChannelSeconds: number;
  /** Seconds of post-respawn damage immunity remaining (see CONFIG.heroRespawnInvulnerableSeconds); 0 most of the time. */
  invulnerableRemaining: number;
}

/** A minion's position in continuous world coordinates — see server/minions.ts. */
export interface MinionSnapshot {
  id: number;
  x: number;
  y: number;
  hp: number;
  maxHp: number;
  isSuper: boolean;
  statuses: StatusSnapshot[];
  /** 0 = just attacked (on full cooldown), 1 = ready to attack again — see HeroSnapshot's field of the same name. */
  attackCooldownPct: number;
}

/** A telegraphed or lingering effect rendered on the ground — meteor/spawn telegraphs and glyph AOEs (design doc's "glyph AOE" area type). `warriorAoePulse` is a brief, purely-visual flash for an instant-cast AOE that has no lingering ground presence (see server/spells.ts's warrior spell 1) — we still want to *show* where and how big it was. */
export type GroundEffectKind = "mageDamageGlyph" | "mageHealGlyph" | "hunterPiercingShot" | "warriorAoePulse" | "meteorTelegraph" | "spawnTelegraph";

export interface GroundEffectSnapshot {
  id: number;
  kind: GroundEffectKind;
  x: number;
  y: number;
  radius: number;
  /** Only set for the brief "hunterPiercingShot" line flash; end point of the line. */
  x2?: number;
  y2?: number;
  remaining: number;
  totalDuration: number;
}

export interface MinionUpgradeLevels {
  minionSpeed: number;
  minionHealth: number;
  minionDamage: number;
  minionCount: number;
}

/** Messages sent from the server to all clients. */
export type ServerMessage =
  | {
      type: "lobby";
      phase: Phase;
      claimedRoles: Partial<Record<Role, true>>;
      heroClasses: Partial<Record<Role, HeroClass>>;
      youAreRole: Role | null;
    }
  | {
      type: "state";
      dungeon: DungeonSnapshot;
      heroes: HeroSnapshot[];
      minions: MinionSnapshot[];
      groundEffects: GroundEffectSnapshot[];
      flameBarrierRow: number;
      architectGodMode: boolean;
      architectGold: number;
      minionUpgradeLevels: MinionUpgradeLevels;
    }
  | { type: "gameOver"; winner: Winner };
